import { useMemo } from "react"
import { useSortedModels, type Model } from "resources/models"
import { usePortfolios, type Portfolio } from "resources/portfolios"
import { useModelsWithCoverage, type ModelWithCoverage } from "resources/model-coverage"
import { type DataStatus } from "helpers/data-statuses"
import { type SelectedInstrument } from "helpers/types/instruments/selected"
import { getCoverageResourceFromInstrument } from "connected-components/dashboard/helpers"

// Stable reference so useSortedModels' memo (and every effect depending on the model list) doesn't
// re-run on every render.
const sortByName = (a: Model, b: Model): number => a.name.localeCompare(b.name)

interface DashboardModelsWithCoverage {
  modelsWithCoverage: (Model | ModelWithCoverage)[] | null
  status: DataStatus
  requiresResourceCoverage: boolean
  portfolios: Portfolio[] | null
  portfoliosStatus: DataStatus
}

/**
 * The one place dashboard model selection resolves its candidate list: the sorted models decorated
 * with the coverage gate for the current instrument, plus the portfolios needed to rank the pick by
 * most-used. Both the selector's default pick and the access fallback consume this so they select
 * from an identical, coverage-aware list. Pass `instrument: null` to skip coverage entirely (the
 * coverage/availability queries then don't fire).
 */
export function useDashboardModelsWithCoverage(
  instrument: SelectedInstrument | null,
  modelId: string | null,
): DashboardModelsWithCoverage {
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [models, modelsStatus] = useSortedModels(sortByName)

  const coverageResource = useMemo(() => getCoverageResourceFromInstrument(instrument), [instrument])
  const portfolioLive = useMemo(
    () => (coverageResource?.resourceType === "portfolio"
      ? (portfolios as Portfolio[] | null)?.find(portfolio => portfolio.id === coverageResource.resourceId)?.live ?? null
      : null),
    [portfolios, coverageResource],
  )

  const { models: modelsWithCoverage, status, requiresResourceCoverage } = useModelsWithCoverage(
    models,
    modelsStatus,
    coverageResource?.resourceType ?? null,
    coverageResource?.resourceId ?? null,
    instrument,
    modelId,
    portfolioLive,
  )

  return { modelsWithCoverage, status, requiresResourceCoverage, portfolios: portfolios as Portfolio[] | null, portfoliosStatus }
}
